import {Timer} from '@fantasmagames/core/src/update/Timer';
import {Module} from '@fantasmagames/slot/src/common/Module';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {BigWinEvents} from '@fantasmagames/slot/src/modules/bigwin/BigWinEvents';
import {BonusGameEvents} from '@fantasmagames/slot/src/modules/bonus/BonusGameEvents';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {InfoEvents} from '@fantasmagames/slot/src/modules/hud/info/InfoEvents';
import {InfoView} from '@fantasmagames/slot/src/modules/hud/info/InfoView';
import {InfoViewEvents} from '@fantasmagames/slot/src/modules/hud/info/InfoViewEvents';
import {InfoModuleBasicState} from '@fantasmagames/slot/src/modules/hud/info/states/InfoModuleBasicState';
import {InfoModuleFreeSpinsState} from '@fantasmagames/slot/src/modules/hud/info/states/InfoModuleFreeSpinsState';
import {SpinButtonEvents} from '@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonEvents';
import {MainMenuEvents} from '@fantasmagames/slot/src/modules/mainmenu/MainMenuEvents';
import {SettingsEvents} from '@fantasmagames/slot/src/modules/mainmenu/settingsmenu/SettingsEvents';
import {AutoplayMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/autoplaymenu/AutoplayMenuEvents';
import {BetlevelMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/betmenu/BetlevelMenuEvents';
import {StartScreenEvents} from '@fantasmagames/slot/src/modules/startscreen/StartScreenEvents';
import {WinPresentationEvents} from '@fantasmagames/slot/src/modules/winpresentation/WinPresentationEvents';
import {RelaxFreeRoundsEvents} from '@fantasmagames/slot/src/modules/relax/freerounds/RelaxFreeRoundsEvents';
import {RelaxLogic} from '@fantasmagames/slot/src/logic/relax/RelaxLogic';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';

/**
 * represents text information on the screen: clock, wins, bets, balance, logo etc.
 */
export class InfoModule extends Module<InfoView> {
	public static readonly BASIC: string = 'basic';
	public static readonly FREESPINS: string = 'freespins';

	public static readonly MULTILINES_COUNTER_DURATION: number = 500;
	public static readonly WIN_CLEAR_DELAY: number = 500;

	protected _delayWinClear: Timer;
	protected _lastWin: number;
	protected _shownWin: boolean;

	public init(): void {
		super.init();

		this.view.setSoundButtonStatus(!this.logic.soundAllActive, false);
	}

	public restore(): void {
		super.restore();

		if (this.logic.isFreeSpinsStarted) {
			this.gotoState(InfoModule.FREESPINS);
		}
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.onView(InfoViewEvents.TOGGLE_ALL_SOUND_OFF, this.onViewToggleAllSoundOff);
		this.onView(InfoViewEvents.TOGGLE_ALL_SOUND_ON, this.onViewToggleAllSoundOn);
		// Passing along these events.
		this.onViewEmitEvent(InfoViewEvents.BALANCE_UPDATED, InfoEvents.BALANCE_UPDATED);
		this.onViewEmitEvent(InfoViewEvents.BET_UPDATED, InfoEvents.BET_UPDATED);
		this.onViewEmitEvent(InfoViewEvents.WIN_UPDATED, InfoEvents.WIN_UPDATED);

		this.on(SettingsEvents.TOGGLE_ALL_SOUND_OFF, this.onToggleAllSoundsOff);
		this.on(SettingsEvents.TOGGLE_ALL_SOUND_ON, this.onToggleAllSoundsOn);

		this.on(SettingsEvents.TOGGLE_MUSIC_ON, this.onToggleMusicOn);
		this.on(SettingsEvents.TOGGLE_MUSIC_OFF, this.onToggleMusicOff);
		this.on(SettingsEvents.TOGGLE_VOICE_ON, this.onToggleVoiceOn);
		this.on(SettingsEvents.TOGGLE_VOICE_OFF, this.onToggleVoiceOff);
		this.on(SettingsEvents.TOGGLE_SOUND_EFFECTS_ON, this.onToggleSoundEffectsOn);
		this.on(SettingsEvents.TOGGLE_SOUND_EFFECTS_OFF, this.onToggleSoundEffectsOff);

		this.onCurrentStateHandle(WinPresentationEvents.INITIAL_WIN_ANIMATION_FINISHED);
		this.onCurrentStateHandle(WinPresentationEvents.COUNTUP_FINISHED);
		this.onCurrentStateHandle(WinPresentationEvents.WIN_NUMBER_SHOWN_TO_PLAYER);

		this.on(MainMenuEvents.MAIN_MENU_OPEN, this.onMainMenuOpen);
		this.on(MainMenuEvents.MAIN_MENU_CLOSED, this.onMainMenuClosed);

		this.on(BetlevelMenuEvents.BET_LEVEL_MENU_OPENED, this.onBetlevelMenuOpened);
		this.on(BetlevelMenuEvents.BET_LEVEL_MENU_CLOSED, this.onBetlevelMenuClosed);

		this.on(AutoplayMenuEvents.AUTO_PLAY_MENU_OPENED, this.onAutoplayMenuOpened);
		this.on(AutoplayMenuEvents.AUTO_PLAY_MENU_CLOSED, this.onAutoplayMenuClosed);

		this.on(StartScreenEvents.EXIT_FINISHED, this.onStartScreenExitFinished);

		this.onCurrentStateHandle(BigWinEvents.FINISHED);
		this.onCurrentStateHandle(FlowModuleEvents.WON_BIG_WIN);
		this.onCurrentStateHandle(FlowModuleEvents.WON);
		this.onCurrentStateHandle(FlowModuleEvents.NO_WIN);
		this.onCurrentStateHandle(FlowModuleEvents.CAN_SPIN);
		this.on(FlowModuleEvents.BONUS_GAME_START, this.onBonusGameStart);
		this.on(FlowModuleEvents.BONUS_GAME_END, this.onBonusGameEnd);

		this.onCurrentStateHandle(BonusGameEvents.BONUS_OUTRO_FINISHED);
		this.onCurrentStateHandle(BigWinEvents.COUNTUP_FINISHED);

		this.onCurrentStateHandle(LogicEvents.SPIN_RESPONSE);
		this.onCurrentStateHandle(LogicEvents.INIT_RESPONSE);

		this.onCurrentStateHandle(SpinButtonEvents.SPIN_BUTTON_PRESSED);
		this.onCurrentStateHandle(SpinButtonEvents.SPIN_PRESSED_NOT_ENOUGH_MONEY);

		this.onCurrentStateHandle(ReelsEvents.ALL_REELS_STOPPED);

		this.on(BetlevelMenuEvents.BET_VALUE_UPDATED, this.onBetValueUpdated);

		this.onCurrentStateHandle(InfoEvents.UPDATE);

		this.onCurrentStateHandle(WinPresentationEvents.INITIAL_ANIMATION_SKIPPED);
		this.onCurrentStateHandle(WinPresentationEvents.INDIVIDUAL_ANIMATION_SKIPPED);

		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinsFinished);
		this.on(FreeSpinsEvents.SHOWING_WIN_SUMMARY_SCREEN, this.onShowingFinalFreeSpinValue);

	}

	public preSpinBalanceUpdate(): void {
		const logic: RelaxLogic = this.logic as RelaxLogic;

		if (!!!logic.freeRoundsData) {
			const balance = logic.preSpinBalance;
			const betValue = logic.betValue;

			const newBalance = balance - betValue;

			if (newBalance >= 0) {
				this.view.setBalance(newBalance);
			}
		}
	}

	public clearWin(): void {
		this._lastWin = 0;
		this.view.setWins(0);
	}

	public updateWin(win: number): void {

		if (!this._shownWin && win === 0) {
			this._shownWin = true;
			win = this._lastWin;

			return;
		}

		this._lastWin = win;

		this.view.setWins(win);
	}

	public updateBalance(): void {
		const balance = this.logic.currentBalance;
		this.view.setBalance(balance);
	}

	public updateBet(): void {
		const bet = this.logic.getTotalBet();
		this.view.setBet(bet);
	}

	protected setupStates(): void {
		this.addState(new InfoModuleBasicState(this, InfoModule.BASIC));
		this.addState(new InfoModuleFreeSpinsState(this, InfoModule.FREESPINS));
	}

	protected enterFirstState(): void {
		this.changeStateTo(InfoModule.BASIC);
	}

	protected setupViews(): void {
		if (this.system.shouldUseMultipleViews()) {
			this.setView(Views.INFO_VIEW_MOBILE);
		} else {
			this.setView(Views.INFO_VIEW);
		}
	}

	protected onShowingFinalFreeSpinValue(): void {
		if (!this.logic.hasRestore) {
			this.view.setBalance(this.logic.currentBalance);

		}
		this.updateBalance();
	}

	protected onFreeSpinsStarted(): void {
		this.gotoState(InfoModule.FREESPINS);
	}

	protected onFreeSpinsFinished(): void {
		this.gotoState(InfoModule.BASIC);
	}

	protected onBetValueUpdated(): void {
		this.updateBet();
		if (!this.logic.hasRestore) {
			this.clearWin();
		}
	}

	protected onViewToggleAllSoundOff(eventId: string, clicked: boolean = true): void {
		this.eventManager.emit(InfoEvents.TOGGLE_ALL_SOUND_OFF, clicked);
	}

	protected onViewToggleAllSoundOn(eventId: string, clicked: boolean = true): void {
		this.eventManager.emit(InfoEvents.TOGGLE_ALL_SOUND_ON, clicked);
	}

	protected onToggleAllSoundsOff(): void {}

	protected onToggleAllSoundsOn(): void {}

	protected onToggleMusicOn(): void {
		if (!this.logic.soundAllActive) {
			this.view.setSoundButtonStatus(false, false);
			this.onViewToggleAllSoundOn(null, false);
		}
	}

	protected onToggleVoiceOn(): void {
		if (!this.logic.soundAllActive) {
			this.view.setSoundButtonStatus(false, false);
			this.onViewToggleAllSoundOn(null, false);
		}
	}

	protected onToggleSoundEffectsOn(): void {
		if (!this.logic.soundAllActive) {
			this.view.setSoundButtonStatus(false, false);
			this.onViewToggleAllSoundOn(null, false);
		}
	}

	protected onToggleMusicOff(): void {
		if (this.logic.soundAllActive && !this.voiceActive && !this.soundActive) {
			this.view.setSoundButtonStatus(true, true);
			this.onViewToggleAllSoundOff(null, false);
		}
	}

	protected onToggleVoiceOff(): void {
		if (this.logic.soundAllActive && !this.soundActive && !this.musicActive) {
			this.view.setSoundButtonStatus(true, true);
			this.onViewToggleAllSoundOff(null, false);
		}
	}

	protected onToggleSoundEffectsOff(): void {
		const voiceActive = this.device.isMobile ? false : this.voiceActive;

		if (this.logic.soundAllActive && !this.musicActive && !voiceActive) {
			this.view.setSoundButtonStatus(true, true);
			this.onViewToggleAllSoundOff(null, false);
		}
	}

	protected onStartScreenExitFinished(): void {
		this.view.playEntryAnimation();
	}

	protected onBetlevelMenuOpened(): void {}

	protected onBetlevelMenuClosed(): void {}

	protected onAutoplayMenuOpened(): void {}

	protected onAutoplayMenuClosed(): void {}

	protected onMainMenuOpen(): void {}

	protected onMainMenuClosed(): void {}

	protected onBonusGameEnd(): void {}

	protected onBonusGameStart(): void {}

	protected get voiceActive(): boolean {
		// This option doesn't exist on mobile.
		return this.device.isMobile ? this.logic.soundEffectsActive : this.logic.voiceActive;
	}

	protected get soundActive(): boolean {
		return this.logic.soundEffectsActive;
	}

	protected get musicActive(): boolean {
		return this.logic.musicActive;
	}

	public get lastWin(): number {
		return this._lastWin;
	}

	public set lastWin(value: number) {
		this._lastWin = value;
	}

	public get shownWin(): boolean {
		return this._shownWin;
	}

	public set shownWin(value: boolean) {
		this._shownWin = value;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/hud/info/InfoModule.ts